import { useLazyQuery } from '@apollo/client'
import { AFForm } from '@faceup/abstract-form'
import type { UseFormReturn } from '@faceup/form'
import { defineMessages, FormattedMessage, useIntl, useLocalization } from '@faceup/localization'
import { type Attachment, followUpVariant, Prompt } from '@faceup/ui'
import { FormItemType, isFormItemTypeSystemType, Plan, RecipientsOnReportForm } from '@faceup/utils'
import { type Schema, yup } from '@faceup/validation'
import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { VoiceRecordingModal } from '../../Components/VoiceRecording/VoiceRecordingModal'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { sharedMessages } from '../../Shared/translations'
import { AttachmentsDropzoneFormItem } from './AttachmentsDropzoneFormItem'
import { AF_ClassroomField } from './components/AF_ClassroomField'
import { AF_FormSkeleton } from './components/AF_FormSkeleton'
import { AF_RecipientsField } from './components/AF_RecipientsField'
import { AF_SenderNameField } from './components/AF_SenderNameField'
import { AF_VictimNameField } from './components/AF_VictimNameField'
import { MoreInformationVoiceField } from './components/MoreInformationVoiceField'
import { useAF_CategorySelect } from './hooks/useAF_CategorySelect'
import { useAF_CreateReportSubmit } from './hooks/useAF_CreateReportSubmit'
import { useAF_OrganizationalUnitSelect } from './hooks/useAF_OrganizationalUnitSelect'
import type { Recipient } from './hooks/useRecipients'
import { useVoiceRecordings } from './hooks/useVoiceRecordings'

const messages = defineMessages('followUp.pages.createReport.aFCreateReportForm', [
  {
    id: 'navigationPrompt',
    defaultMessage: 'Data you have entered will be lost if you leave this page.',
  },
  { id: 'noFormItems', defaultMessage: 'No form items configured for this category.' },
  { id: 'submitErrorSummary', defaultMessage: 'Please fill in all required fields.' },
  {
    id: 'moreInfoDetailSchool',
    defaultMessage:
      'To help effectively, it is important that we understand what the behavior you are submitting looks like. Could you please write where and when this happened?',
  },
  {
    id: 'moreInfoDetailCompany',
    defaultMessage:
      'Please provide specific details: names, dates, frequency, and if management was notified. Distinguish facts from assumptions. Complete information is vital for a thorough investigation.',
  },
  { id: 'voiceRecordingLabel', defaultMessage: 'Voice message {number}', values: { number: true } },
  { id: 'voiceRecordingButtonLabel', defaultMessage: 'Record a voice message' },
  {
    id: 'voiceRecordingButtonMaxReachedLabel',
    defaultMessage: 'You can add a maximum of 3 recordings.',
  },
] as const)

export const fragments = {
  AF_CreateReportForm_categories: graphql(`
    fragment AF_CreateReportForm_categories on PublicReportSource {
      id
      categories {
        id
        name
      }
      identityExcludedOrganizationalUnitPublicIds
      isAnonymityEnabled
      organizationalUnits {
        id
        organizationalUnitName
      }
      config {
        ... on FormReportSourceConfiguration {
          isAnonymityPreselected
          recipientsOnReportForm
          attachmentsEnabled
          voiceSubmissionEnabled
        }
      }
    }
  `),
  AF_CreateReportForm_institution: graphql(`
    fragment AF_CreateReportForm_institution on PublicCompany {
      id
      isE2EE
      partner {
        id
        name
      }
      config {
        id
        institutionName
      }
      billing {
        id
        plan
      }
      ...AttachmentsDropzoneFormItem_institution
    }
  `),
}

const queries = {
  AF_CreateReportForm_formItemsQuery: graphql(`
    query AF_CreateReportForm_formItems(
      $formId: UUID!
      $categoryId: CompanyReportCategoryGlobalId!
      $language: Language
    ) {
      AF_formItemsForCategory(formId: $formId, categoryId: $categoryId) {
        ... on AF_FormItemInterface {
          id
          isHidden
          type
          ...AFForm_item
        }
        ... on AF_FormItemCategory {
          isRequired
          hintTranslation(language: $language) {
            translation
          }
          labelTranslation(language: $language) {
            translation
          }
          options {
            id
            order
            translation(language: $language) {
              translation
              language
            }
          }
        }
        ... on AF_FormItemSenderName {
          isRequired
          hintTranslation(language: $language) {
            translation
          }
          labelTranslation(language: $language) {
            translation
          }
        }
        ... on AF_FormItemOrganizationalUnit {
          isRequired
          hintTranslation(language: $language) {
            translation
          }
          labelTranslation(language: $language) {
            translation
          }
        }
        ... on AF_FormItemClassroom {
          isRequired
          hintTranslation(language: $language) {
            translation
          }
          labelTranslation(language: $language) {
            translation
          }
        }
      }
    }
  `),
}

type Props = {
  formId: string
  categories: FragmentType<typeof fragments.AF_CreateReportForm_categories>
  institution: FragmentType<typeof fragments.AF_CreateReportForm_institution>
  isSchoolDefaultForm?: boolean
  publicKey: string
  onSent: (pin: string | null, tag: string) => void
}

export const AF_CreateReportForm = ({
  formId,
  categories,
  institution,
  isSchoolDefaultForm = false,
  publicKey,
  onSent,
}: Props) => {
  const categoriesData = getFragmentData(fragments.AF_CreateReportForm_categories, categories)
  const institutionData = getFragmentData(fragments.AF_CreateReportForm_institution, institution)
  const { formatMessage } = useIntl()
  const { language } = useLocalization()
  const { isAnonymous, setIsAnonymous } = useContext(ReportFormContext)
  const voiceRecordings = useVoiceRecordings()

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<string | undefined>(undefined)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  const [getFormItems, { loading: formItemsLoading, data: formItemsData }] = useLazyQuery(
    queries.AF_CreateReportForm_formItemsQuery
  )

  const handleCategoryChange = useCallback(
    (selectedCategoryId: string) => {
      setSelectedCategory(selectedCategoryId)
      getFormItems({
        variables: {
          formId,
          categoryId: selectedCategoryId,
          language,
        },
      })
    },
    [formId, language, getFormItems]
  )

  useEffect(() => {
    const firstCategoryId = categoriesData.categories[0]?.id
    if (firstCategoryId && !formItemsData) {
      getFormItems({
        variables: {
          formId,
          categoryId: firstCategoryId,
          language,
        },
      })
    }
  }, [formId, categoriesData.categories, getFormItems, language, formItemsData])

  useEffect(() => {
    if (
      categoriesData.categories.length === 1 &&
      categoriesData.categories[0]?.id &&
      !selectedCategory
    ) {
      const singleCategoryId = categoriesData.categories[0].id
      setSelectedCategory(singleCategoryId)
    }
  }, [categoriesData.categories, selectedCategory])

  const formItems = formItemsData?.AF_formItemsForCategory ?? []

  const { categoryItem, senderNameItem, organizationalUnitItem, classroomItem, moreInfoItem } =
    useMemo(() => {
      const categoryItem = formItems.find(
        (item): item is typeof item & { __typename: 'AF_FormItemCategory' } =>
          item.__typename === 'AF_FormItemCategory'
      )
      const senderNameItem = formItems.find(
        (item): item is typeof item & { __typename: 'AF_FormItemSenderName' } =>
          item.__typename === 'AF_FormItemSenderName' && item.type === FormItemType.SenderName
      )
      const organizationalUnitItem = formItems.find(
        (item): item is typeof item & { __typename: 'AF_FormItemOrganizationalUnit' } =>
          item.__typename === 'AF_FormItemOrganizationalUnit' &&
          item.type === FormItemType.OrganizationalUnit
      )
      const classroomItem = formItems.find(
        (item): item is typeof item & { __typename: 'AF_FormItemClassroom' } =>
          item.__typename === 'AF_FormItemClassroom' && item.type === FormItemType.Classroom
      )
      const moreInfoItem = formItems.find(item => item.type === FormItemType.MoreInformation)

      return { categoryItem, senderNameItem, organizationalUnitItem, classroomItem, moreInfoItem }
    }, [formItems])

  const categoryField = useAF_CategorySelect({
    categoryItem,
    categories: categoriesData.categories,
    selectedCategory,
    formItemsLoading,
    onCategoryChange: handleCategoryChange,
  })

  const organizationalUnitField = useAF_OrganizationalUnitSelect({
    organizationalUnitItem,
    selectableOrganizationUnits: categoriesData.organizationalUnits ?? [],
    selectedOrgUnit,
    onSelectionChange: setSelectedOrgUnit,
  })

  const formConfig =
    categoriesData.config?.__typename === 'FormReportSourceConfiguration'
      ? categoriesData.config
      : null

  const isAnonymityEnabledBase = categoriesData.isAnonymityEnabled ?? true

  const isAnonymityPreselected = formConfig?.isAnonymityPreselected ?? true

  const recipientsOnReportForm =
    formConfig?.recipientsOnReportForm ?? RecipientsOnReportForm.Visible

  const attachmentsEnabled = formConfig?.attachmentsEnabled ?? true

  const voiceSubmissionEnabled = formConfig?.voiceSubmissionEnabled ?? true

  const canUseVoiceRecordButton = useMemo(() => {
    const isVoiceSubmissionAllowed =
      !isSchoolDefaultForm || institutionData?.billing?.plan === Plan.Standard
    return isVoiceSubmissionAllowed && voiceSubmissionEnabled
  }, [isSchoolDefaultForm, institutionData?.billing?.plan, voiceSubmissionEnabled])

  useEffect(() => {
    setIsAnonymous(isAnonymityEnabledBase && isAnonymityPreselected)
  }, [isAnonymityPreselected, isAnonymityEnabledBase, setIsAnonymous])

  // Mirrors the BE definition of system form item types (packages/utils/src/constants.ts):
  // these items are either not part of the encrypted answers payload (Category, OrganizationalUnit,
  // Classroom) or are handled through dedicated paths (MoreInformation, SenderName).
  const excludedItemIds = useMemo(
    () => formItems.filter(item => isFormItemTypeSystemType(item.type)).map(item => item.id),
    [formItems]
  )

  const { submit } = useAF_CreateReportSubmit({
    formId,
    publicKey,
    isE2EE: institutionData?.isE2EE ?? false,
    onSent,
  })

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      const orgUnitFormValue = organizationalUnitItem?.id
        ? (values[organizationalUnitItem.id] as { value: string } | undefined)
        : undefined
      const organizationalUnitId =
        orgUnitFormValue?.value ?? categoriesData.organizationalUnits[0]?.id ?? ''

      const recipients = (values['recipients'] as Recipient[]) ?? []

      const victimNameFormValue = values['victimName'] as { value: string } | undefined
      const victimNameValue = isSchoolDefaultForm ? victimNameFormValue?.value?.trim() : undefined

      const isAnonymityEnabled =
        isAnonymityEnabledBase &&
        !categoriesData.identityExcludedOrganizationalUnitPublicIds.includes(organizationalUnitId)

      const success = await submit({
        values,
        recipients,
        categoryId: selectedCategory ?? '',
        organizationalUnitId,
        excludedItemIds,
        isAnonymous,
        isAnonymityEnabled,
        isAnonymityPreselected,
        isSchoolDefaultForm,
        moreInfoItemId: moreInfoItem?.id,
        senderNameItemId: senderNameItem?.id,
        victimNameValue,
        voiceRecordingFiles: voiceRecordings.files,
      })

      if (success) {
        setIsFormSubmitted(true)
        voiceRecordings.reset()
      }

      return success
    },
    [
      submit,
      selectedCategory,
      excludedItemIds,
      isAnonymous,
      isAnonymityEnabledBase,
      isAnonymityPreselected,
      categoriesData.identityExcludedOrganizationalUnitPublicIds,
      moreInfoItem?.id,
      senderNameItem?.id,
      organizationalUnitItem?.id,
      categoriesData.organizationalUnits,
      isSchoolDefaultForm,
      voiceRecordings.files,
      voiceRecordings.reset,
    ]
  )

  const isCheckPromptNeeded = useMemo(
    () => (!selectedCategory || attachments.length > 0) && !isFormSubmitted,
    [selectedCategory, attachments.length, isFormSubmitted]
  )

  const orgUnitResolved =
    !organizationalUnitItem ||
    (categoriesData.organizationalUnits?.length ?? 0) <= 1 ||
    Boolean(selectedOrgUnit)
  const showFormBody = Boolean(selectedCategory) && orgUnitResolved

  const headerFields = useMemo(() => {
    const fields = []

    if (categoryField) {
      fields.push(categoryField)
    }

    if (organizationalUnitField) {
      fields.push(organizationalUnitField)
    }

    if (
      recipientsOnReportForm !== RecipientsOnReportForm.Invisible &&
      selectedCategory &&
      showFormBody
    ) {
      fields.push({
        id: 'recipients',
        schema: yup.array().of(
          yup.object({
            id: yup.string().required(),
            publicKey: yup.string().nullable(),
            isSelected: yup.boolean().required(),
          })
        ),
        defaultValue: [],
        render: (form: UseFormReturn) => (
          <AF_RecipientsField
            form={form}
            categoryItemId={categoryItem?.id}
            organizationalUnitItemId={organizationalUnitItem?.id}
            reportSourceId={categoriesData.id}
            recipientsOnReportForm={recipientsOnReportForm}
            institutionData={institutionData}
          />
        ),
      })
    }

    return fields
  }, [
    categoryField,
    organizationalUnitField,
    categoryItem?.id,
    organizationalUnitItem?.id,
    showFormBody,
    recipientsOnReportForm,
    categoriesData.id,
    institutionData,
    selectedCategory,
  ])

  const attachmentsField = useMemo(() => {
    if (!attachmentsEnabled) {
      return null
    }

    return {
      id: 'attachments',
      schema: yup.array(),
      defaultValue: [],
      render: (form: UseFormReturn): ReactNode => (
        <AttachmentsDropzoneFormItem
          institution={institutionData}
          attachments={attachments}
          onSetAttachments={newAttachments => {
            setAttachments(newAttachments)
            form.setValue('attachments', newAttachments)
          }}
        />
      ),
    }
  }, [attachmentsEnabled, institutionData, attachments])

  const footerFields = useMemo(() => {
    const fields: {
      id: string
      schema: Schema
      defaultValue: unknown
      render: (form: UseFormReturn) => ReactNode
    }[] = []

    if (attachmentsField) {
      fields.push(attachmentsField)
    }

    if (isSchoolDefaultForm) {
      const victimNameFieldId = 'victimName'
      fields.push({
        id: victimNameFieldId,
        schema: yup.object({
          type: yup.string().oneOf(['VictimName']).required(),
          value: yup.string().max(255).required(),
        }),
        defaultValue: {
          type: 'VictimName',
          value: undefined,
        },
        render: (form: UseFormReturn) => (
          <AF_VictimNameField form={form} victimNameFieldId={victimNameFieldId} />
        ),
      })
    }

    if (isSchoolDefaultForm && classroomItem) {
      fields.push({
        id: classroomItem.id,
        schema: yup.object({
          type: yup.string().oneOf([FormItemType.Classroom]).required(),
          value: classroomItem.isRequired ? yup.string().required() : yup.string(),
        }),
        defaultValue: {
          type: FormItemType.Classroom,
          value: undefined,
        },
        render: (form: UseFormReturn) => (
          <AF_ClassroomField form={form} classroomItem={classroomItem} />
        ),
      })
    }

    if (!isSchoolDefaultForm && senderNameItem) {
      const senderNameSchema = yup.object({
        type: yup.string().oneOf([FormItemType.SenderName]).required(),
        value: yup
          .string()
          .when('$isAnonymous', ([isAnon], schema) =>
            isAnon ? schema : schema.max(255).required()
          ),
      })

      const senderNameLabel =
        senderNameItem.labelTranslation?.translation ??
        formatMessage(sharedMessages.globalNameLabel)
      const senderNameHint = senderNameItem.hintTranslation?.translation

      fields.push({
        id: senderNameItem.id,
        schema: senderNameSchema,
        defaultValue: {
          type: FormItemType.SenderName,
          value: undefined,
        },
        render: (form: UseFormReturn) => {
          const orgUnitValue = organizationalUnitItem?.id
            ? (form.watch(`${organizationalUnitItem.id}.value`) as string | undefined)
            : undefined

          const isAnonymityEnabled =
            isAnonymityEnabledBase &&
            !categoriesData.identityExcludedOrganizationalUnitPublicIds.includes(orgUnitValue ?? '')

          return (
            <AF_SenderNameField
              form={form}
              senderNameFieldId={senderNameItem.id}
              senderNameLabel={senderNameLabel}
              senderNameHint={senderNameHint}
              isAnonymous={isAnonymous}
              setIsAnonymous={setIsAnonymous}
              isAnonymityEnabled={isAnonymityEnabled}
              isSchoolDefaultForm={isSchoolDefaultForm}
            />
          )
        },
      })
    }

    return fields
  }, [
    attachmentsField,
    senderNameItem,
    classroomItem,
    isSchoolDefaultForm,
    formatMessage,
    organizationalUnitItem?.id,
    isAnonymityEnabledBase,
    categoriesData.identityExcludedOrganizationalUnitPublicIds,
    isAnonymous,
    setIsAnonymous,
  ])

  if (formItemsLoading || !categoryItem) {
    return <AF_FormSkeleton variant='small' />
  }

  return (
    <div className='flex flex-col gap-4'>
      <Prompt when={isCheckPromptNeeded} message={formatMessage(messages.navigationPrompt)} />
      {!showFormBody && (
        <AFForm
          items={[]}
          headerFields={headerFields}
          excludedItemIds={excludedItemIds}
          variant={followUpVariant}
          hideSubmitButtons
          onSubmit={async () => false}
        />
      )}
      {showFormBody && formItemsLoading && <AF_FormSkeleton variant='large' />}
      {showFormBody && !formItemsLoading && formItems.length > 0 && (
        <AFForm
          items={formItems}
          onSubmit={handleSubmit}
          headerFields={headerFields}
          footerFields={footerFields}
          excludedItemIds={excludedItemIds}
          validationContext={{
            isAnonymous,
            hasMoreInformationVoiceRecording: voiceRecordings.recordings.length > 0,
          }}
          submitErrorMessage={<FormattedMessage message={messages.submitErrorSummary} />}
          submitErrorMessageWrapperClassName='bg-colorFillTertiary px-6 -mt-6'
          buttonsPosition={!isSchoolDefaultForm && senderNameItem ? 'under-block' : 'under-center'}
          submitButtonWrapperClassName={
            !isSchoolDefaultForm && senderNameItem
              ? 'bg-colorFillTertiary rounded-b-[20px] px-6 pb-8 pt-4 -mt-6'
              : undefined
          }
          variant={followUpVariant}
          renderOverrides={{
            [FormItemType.MoreInformation]: ({ commonProps }) => (
              <MoreInformationVoiceField
                commonProps={{
                  ...commonProps,
                  hint:
                    commonProps.hint ||
                    formatMessage(
                      isSchoolDefaultForm
                        ? messages.moreInfoDetailSchool
                        : messages.moreInfoDetailCompany
                    ),
                }}
                voiceRecordings={voiceRecordings}
                voiceSubmissionEnabled={canUseVoiceRecordButton}
                voiceRecordingButtonLabel={formatMessage(messages.voiceRecordingButtonLabel)}
                voiceRecordingButtonMaxReachedLabel={formatMessage(
                  messages.voiceRecordingButtonMaxReachedLabel
                )}
                recordingLabel={n =>
                  formatMessage(messages.voiceRecordingLabel, { values: { number: n } })
                }
              />
            ),
          }}
        />
      )}
      {showFormBody && !formItemsLoading && formItems.length === 0 && (
        <div className='text-center text-gray-500 py-8'>
          <FormattedMessage message={messages.noFormItems} />
        </div>
      )}
      <VoiceRecordingModal
        isVisible={voiceRecordings.isModalOpen}
        onClose={voiceRecordings.closeModal}
        onRecordingSave={voiceRecordings.handleRecordingSaved}
      />
    </div>
  )
}
